import apiClient from './api'
import { KPIMetric } from '@/components/widgets/types'

export interface KPISummary {
  deposits: {
    current: number
    target?: number
    previous?: number
    trend?: {
      direction: 'up' | 'down' | 'neutral'
      percentage: number
    }
  }
  withdrawals: {
    current: number
    target?: number
    previous?: number
    trend?: {
      direction: 'up' | 'down' | 'neutral'
      percentage: number
    }
  }
  netFlow: {
    current: number
    target?: number
    previous?: number
    trend?: {
      direction: 'up' | 'down' | 'neutral'
      percentage: number
    }
  }
  successRate: {
    current: number
    target?: number
    previous?: number
    trend?: {
      direction: 'up' | 'down' | 'neutral'
      percentage: number
    }
  }
  transactionCount: {
    current: number
    target?: number
    previous?: number
    trend?: {
      direction: 'up' | 'down' | 'neutral'
      percentage: number
    }
  }
  activeUsers: {
    current: number
    target?: number
    previous?: number
    trend?: {
      direction: 'up' | 'down' | 'neutral'
      percentage: number
    }
  }
}

export const kpiService = {
  fetchKPISummary: async (hours: number = 24): Promise<KPISummary> => {
    // For now, use dashboard summary endpoint
    // TODO: Create dedicated KPI endpoint with targets
    const response = await apiClient.get('/reports/dashboard-summary', { params: { hours } })
    const data = response.data

    // Calculate trends if previous period data is available
    const calculateTrend = (current: number, previous?: number) => {
      if (!previous || previous === 0) return undefined
      const change = ((current - previous) / previous) * 100
      return {
        direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
        percentage: Math.abs(change),
      }
    }

    return {
      deposits: {
        current: data.totals?.deposits || 0,
        previous: data.totals?.deposits || 0, // TODO: Get from previous period
        trend: (() => {
          const trend = calculateTrend(data.totals?.deposits || 0, data.totals?.deposits || 0)
          return trend ? {
            direction: trend.direction as 'up' | 'down' | 'neutral',
            percentage: trend.percentage
          } : undefined
        })(),
      },
      withdrawals: {
        current: data.totals?.withdrawals || 0,
        previous: data.totals?.withdrawals || 0,
        trend: (() => {
          const trend = calculateTrend(data.totals?.withdrawals || 0, data.totals?.withdrawals || 0)
          return trend ? {
            direction: trend.direction as 'up' | 'down' | 'neutral',
            percentage: trend.percentage
          } : undefined
        })(),
      },
      netFlow: {
        current: data.totals?.net || 0,
        previous: data.totals?.net || 0,
        trend: (() => {
          const trend = calculateTrend(data.totals?.net || 0, data.totals?.net || 0)
          return trend ? {
            direction: trend.direction as 'up' | 'down' | 'neutral',
            percentage: trend.percentage
          } : undefined
        })(),
      },
      successRate: {
        current: data.successRate?.current || 0,
        previous: data.successRate?.previous || 0,
        trend: data.successRate?.change
          ? {
              direction: data.successRate.change > 0 ? 'up' : data.successRate.change < 0 ? 'down' : 'neutral',
              percentage: Math.abs(data.successRate.change),
            }
          : undefined,
      },
      transactionCount: {
        current: data.totals?.transactions || 0,
        previous: data.totals?.transactions || 0,
        trend: (() => {
          const trend = calculateTrend(data.totals?.transactions || 0, data.totals?.transactions || 0)
          return trend ? {
            direction: trend.direction as 'up' | 'down' | 'neutral',
            percentage: trend.percentage
          } : undefined
        })(),
      },
      activeUsers: {
        current: data.totals?.activeUsers || 0,
        previous: data.totals?.activeUsers || 0,
        trend: (() => {
          const trend = calculateTrend(data.totals?.activeUsers || 0, data.totals?.activeUsers || 0)
          return trend ? {
            direction: trend.direction as 'up' | 'down' | 'neutral',
            percentage: trend.percentage
          } : undefined
        })(),
      },
    }
  },

  getKPIMetrics: async (hours: number = 24): Promise<KPIMetric[]> => {
    const summary = await kpiService.fetchKPISummary(hours)

    return [
      {
        id: 'deposits',
        label: 'Total Deposits',
        value: summary.deposits.current,
        target: summary.deposits.target,
        previousValue: summary.deposits.previous,
        format: 'currency',
        trend: summary.deposits.trend,
      },
      {
        id: 'withdrawals',
        label: 'Total Withdrawals',
        value: summary.withdrawals.current,
        target: summary.withdrawals.target,
        previousValue: summary.withdrawals.previous,
        format: 'currency',
        trend: summary.withdrawals.trend,
      },
      {
        id: 'net-flow',
        label: 'Net Flow',
        value: summary.netFlow.current,
        target: summary.netFlow.target,
        previousValue: summary.netFlow.previous,
        format: 'currency',
        trend: summary.netFlow.trend,
      },
      {
        id: 'success-rate',
        label: 'Success Rate',
        value: summary.successRate.current,
        target: summary.successRate.target || 95,
        previousValue: summary.successRate.previous,
        format: 'percentage',
        trend: summary.successRate.trend,
      },
      {
        id: 'transaction-count',
        label: 'Transactions',
        value: summary.transactionCount.current,
        target: summary.transactionCount.target,
        previousValue: summary.transactionCount.previous,
        format: 'number',
        trend: summary.transactionCount.trend,
      },
      {
        id: 'active-users',
        label: 'Active Users',
        value: summary.activeUsers.current,
        target: summary.activeUsers.target,
        previousValue: summary.activeUsers.previous,
        format: 'number',
        trend: summary.activeUsers.trend,
      },
    ]
  },
}

